import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Play, Star, ChevronRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Share() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [error, setError] = useState(false);
  const sp = new URLSearchParams(window.location.search);
  const refCode = sp.get("ref") || "";
  // Pass through ref + UTM tags + the originating share video for downstream attribution
  const passthroughKeys = ["ref", "utm_source", "utm_medium", "utm_campaign", "utm_content"];
  const passthrough = new URLSearchParams();
  passthroughKeys.forEach((k) => { const v = sp.get(k); if (v) passthrough.set(k, v); });
  passthrough.set("share_video_id", id);
  const refQs = `?${passthrough.toString()}`;

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/title/${id}`)
      .then((r) => setV(r.data))
      .catch(() => setError(true));
  }, [id]);

  const copy = async () => {
    try {
      const shareUrl = `${window.location.origin}/api/og/${id}${refQs}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied!");
    } catch {
      toast.error("Could not copy");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center p-6">
        <h1 className="text-2xl mb-2">Title not found</h1>
        <Link to="/" className="text-[#E50914] hover:underline">Back to home</Link>
      </div>
    );
  }
  if (!v) return <div className="min-h-screen bg-[#0A0A0A]" />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" data-testid="share-logo" className="text-[#E50914] font-black text-2xl tracking-tight">CINECINEMA</Link>
        <div className="flex items-center gap-3">
          <button data-testid="share-copy-btn" onClick={copy} className="btn-glass px-4 py-2 rounded font-semibold text-sm flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Copy link
          </button>
          <button data-testid="share-signin-btn" onClick={() => nav("/login")} className="btn-glass px-5 py-2 rounded font-semibold text-sm">
            Sign In
          </button>
        </div>
      </header>

      {/* Hero with trailer */}
      <section className="relative h-[88vh] min-h-[560px] overflow-hidden">
        {v.trailer_yt ? (
          <iframe
            data-testid="share-trailer"
            className="absolute inset-0 w-full h-full pointer-events-none"
            src={`https://www.youtube.com/embed/${v.trailer_yt}?autoplay=1&mute=1&loop=1&controls=0&playlist=${v.trailer_yt}&modestbranding=1&playsinline=1&rel=0`}
            allow="autoplay; encrypted-media"
            title={v.title}
          />
        ) : (
          <img src={v.backdrop} alt={v.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-20 px-6 lg:px-12 max-w-3xl">
          <span className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-3">Watch the trailer</span>
          <h1 data-testid="share-title" className="text-5xl md:text-7xl font-black tracking-tighter">{v.title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-300">
            <span className="flex items-center gap-1 text-yellow-400 font-semibold"><Star className="w-4 h-4 fill-current" /> {v.tmdb_rating}</span>
            <span>{v.year}</span>
            <span className="border border-white/30 px-2 py-0.5 text-xs">{v.rating}</span>
            <span>{v.duration_min} min</span>
            <span className="capitalize">{v.type}</span>
          </div>
          <p className="mt-4 text-lg text-gray-200 line-clamp-3 max-w-2xl">{v.description}</p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button
              data-testid="share-watch-btn"
              onClick={() => nav(`/signup${refQs}`)}
              className="btn-red font-semibold px-8 py-3 rounded flex items-center gap-2 text-lg"
            >
              <Play className="w-5 h-5 fill-current" /> Watch full title
            </button>
            <button
              onClick={() => nav("/pricing")}
              className="btn-glass font-semibold px-8 py-3 rounded text-lg"
            >
              See plans
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">Free trial · Cancel anytime · 4K available</p>
        </div>
      </section>

      {/* About + CTA */}
      <section className="px-6 lg:px-12 py-12 max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-3">
          <h2 className="text-xl font-semibold">About {v.title}</h2>
          <p className="text-gray-300">{v.description}</p>
          <p className="text-sm text-gray-400"><span className="text-gray-500">Genres:</span> {v.genres?.join(", ")}</p>
          {v.cast?.length > 0 && (
            <p className="text-sm text-gray-400"><span className="text-gray-500">Cast:</span> {v.cast.join(", ")}</p>
          )}
        </div>
        <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-2">Loved the trailer?</h3>
          <p className="text-sm text-gray-400 mb-4">Join CineCinema to watch the full title plus thousands more in HD & 4K.</p>
          <Link
            to={`/signup${refQs}`}
            data-testid="share-cta-signup"
            className="btn-red w-full inline-flex items-center justify-center gap-2 py-3 rounded font-semibold"
          >
            Start free trial <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6 lg:px-12 text-center text-sm text-gray-500">
        <p>© 2026 CineCinema. <Link to="/" className="hover:text-white">Home</Link></p>
      </footer>
    </div>
  );
}
